import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FiSearch } from 'react-icons/fi';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardHeader, CardTitle, CardBody } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { Select } from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { theme } from '../../../theme';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Complain,
  ComplainStatus,
  COMPLAIN_STATUSES,
  COMPANY_COMPLAIN_STATUSES,
  complainDateToJs,
  effectiveCompletedAtForDetail,
  effectiveResolvedAtForDetail,
  formatComplainDisplayDate,
  formatComplainStatusLabel,
  subscribeAllComplainsForCompany,
  updateComplainStatus,
} from '../service/complainService';
import { useClientPagination } from '../../../hooks/useClientPagination';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  max-width: 560px;
`;

const FilterCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FilterCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const Sub = styled.p`
  margin: ${theme.spacing[2]} 0 0 0;
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

const DescText = styled.span`
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  min-width: 0;
  max-width: 320px;
`;

const CellPrimary = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
  word-break: break-word;
`;

const CellMuted = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CellMutedWrap = styled(CellMuted)`
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing[4]};
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

const StatusCell = styled.div`
  min-width: 160px;
  max-width: 220px;
`;

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const StatusBadge = styled.span<{
  variant?: 'open' | 'progress' | 'resolved' | 'closed' | 'completed' | 'rejected';
}>`
  display: inline-block;
  align-self: flex-start;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant = 'open' }) => {
    switch (variant) {
      case 'progress':
        return `${theme.colors.primary[500]}20`;
      case 'resolved':
      case 'completed':
        return `${theme.colors.accent.success}22`;
      case 'rejected':
        return `${theme.colors.accent.danger}18`;
      case 'closed':
        return `${theme.colors.light.background.tertiary}`;
      default:
        return `${theme.colors.secondary[200]}`;
    }
  }};
  color: ${({ variant = 'open' }) => {
    switch (variant) {
      case 'progress':
        return theme.colors.primary[700];
      case 'resolved':
      case 'completed':
        return theme.colors.accent.success;
      case 'rejected':
        return theme.colors.accent.danger;
      case 'closed':
        return theme.colors.light.text.secondary;
      default:
        return theme.colors.light.text.primary;
    }
  }};
`;

const DetailGrid = styled.div`
  display: grid;
  gap: ${theme.spacing[4]};
`;

const DetailField = styled.div``;

const DetailLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.secondary};
  margin-bottom: ${theme.spacing[1]};
`;

const DetailValue = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  white-space: pre-wrap;
  word-break: break-word;
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: ${theme.typography.fontSize.sm};
`;

function formatStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function allStatusesSelected(): Record<ComplainStatus, boolean> {
  return COMPLAIN_STATUSES.reduce(
    (acc, s) => {
      acc[s] = true;
      return acc;
    },
    {} as Record<ComplainStatus, boolean>
  );
}

function rowMatchesSearch(r: Complain, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [r.id, r.type, r.description, r.bankName, r.address, r.branchCode]
    .filter((x) => x != null && String(x).trim() !== '')
    .some((x) => String(x).toLowerCase().includes(t));
}

function clientDetailPrimary(r: Complain): string {
  const code = r.branchCode != null && String(r.branchCode).trim() !== '' ? String(r.branchCode).trim() : '';
  if (code) return code;
  return r.bankName?.trim() || r.address?.trim() || '—';
}

function clientDetailSecondary(r: Complain): string {
  const code = r.branchCode != null && String(r.branchCode).trim() !== '' ? String(r.branchCode).trim() : '';
  const bank = r.bankName?.trim() || '';
  const addr = r.address?.trim() || '';
  if (code) {
    return [bank, addr].filter(Boolean).join(' — ');
  }
  if (bank && addr) return addr;
  return '';
}

const CompanyComplainsPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyData } = useAuth();
  const [rows, setRows] = useState<Complain[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Complain | null>(null);
  const [statusSavingId, setStatusSavingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [statusFilters, setStatusFilters] = useState<Record<ComplainStatus, boolean>>(() =>
    allStatusesSelected()
  );

  const allowed = companyData?.id && companyId === companyData.id;

  const displayRows = useMemo(() => {
    return rows.filter((r) => statusFilters[r.status] && rowMatchesSearch(r, search));
  }, [rows, statusFilters, search]);

  const activeFilterCount = useMemo(
    () => COMPLAIN_STATUSES.filter((s) => !statusFilters[s]).length,
    [statusFilters]
  );

  const {
    pagedItems: pagedComplains,
    page: complainPage,
    setPage: setComplainPage,
    totalPages: complainTotalPages,
    usePaging: complainUsePaging,
    rangeStart: complainRangeStart,
    rangeEnd: complainRangeEnd,
    totalItems: complainTotalItems,
  } = useClientPagination(displayRows, displayRows.length, search, activeFilterCount);

  useEffect(() => {
    if (!allowed || !companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = subscribeAllComplainsForCompany(
      companyId,
      (data) => {
        setRows(data);
        setLoading(false);
        setDetail((d) => {
          if (!d) return d;
          const found = data.find(
            (x) => x.id === d.id && x.bankId === d.bankId && x.branchId === d.branchId
          );
          return found ?? d;
        });
      },
      (err) => {
        console.error(err);
        setRows([]);
        setLoading(false);
      }
    );
    return unsub;
  }, [allowed, companyId]);

  useEffect(() => {
    const openId = searchParams.get('openComplain');
    if (!openId || rows.length === 0 || !companyId) return;
    const found = rows.find((r) => r.id === openId);
    if (!found) return;
    setDetail(found);
    const next = new URLSearchParams(searchParams);
    next.delete('openComplain');
    setSearchParams(next, { replace: true });
  }, [rows, searchParams, companyId, setSearchParams]);

  const handleStatusChange = async (row: Complain, next: ComplainStatus) => {
    if (!companyId || next === row.status) return;
    try {
      setStatusSavingId(row.id);
      await updateComplainStatus(companyId, row.bankId, row.branchId, row.id, next);
      toast.success('Status updated');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to update status');
    } finally {
      setStatusSavingId(null);
    }
  };

  if (!companyId) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>Invalid route.</CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  if (!allowed) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>You do not have access to this company&apos;s complains.</CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <div>
          <PageTitle>Complains</PageTitle>
          <Sub>Review complains from bank branches, update status, or open a row for details.</Sub>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>All complains</CardTitle>
        </CardHeader>
        <CardBody>
          {!loading && rows.length > 0 ? (
            <Toolbar>
              <div
                style={{
                  flex: '1 1 220px',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing[2],
                  color: theme.colors.light.text.secondary,
                }}
              >
                <FiSearch size={20} aria-hidden />
                <Input
                  type="text"
                  placeholder="Search ID, type, description, branch…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  dark
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setFilterModalOpen(true)}
                aria-label="Filter complains"
                title="Filters"
              >
                <svg
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                  style={{ marginRight: activeFilterCount > 0 ? theme.spacing[2] : 0, flexShrink: 0 }}
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                Filter
                {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Button>
            </Toolbar>
          ) : null}
          {loading ? (
            <EmptyState>
              <EmptyStateText>Loading…</EmptyStateText>
            </EmptyState>
          ) : rows.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>📋</EmptyStateIcon>
              <EmptyStateText>No complains yet.</EmptyStateText>
            </EmptyState>
          ) : displayRows.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>🔎</EmptyStateIcon>
              <EmptyStateText>No complains match your search or filters.</EmptyStateText>
            </EmptyState>
          ) : (
            <>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Complain ID</Table.HeaderCell>
                  <Table.HeaderCell>Client</Table.HeaderCell>
                  <Table.HeaderCell>Type &amp; description</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Note</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {pagedComplains.map((r) => (
                  <Table.Row
                    key={`${r.bankId}-${r.branchId}-${r.id}`}
                    hover
                    onClick={() => setDetail(r)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Cell>
                      <CellStack style={{ maxWidth: 200 }}>
                        <Mono style={{ fontSize: theme.typography.fontSize.sm }}>{r.id}</Mono>
                        <CellMuted>{formatComplainDisplayDate(r.createdAt)}</CellMuted>
                      </CellStack>
                    </Table.Cell>
                    <Table.Cell>
                      <CellStack>
                        <CellPrimary title={clientDetailPrimary(r)}>{clientDetailPrimary(r)}</CellPrimary>
                        {clientDetailSecondary(r) ? (
                          <CellMutedWrap title={clientDetailSecondary(r)}>
                            {clientDetailSecondary(r)}
                          </CellMutedWrap>
                        ) : null}
                      </CellStack>
                    </Table.Cell>
                    <Table.Cell>
                      <CellStack>
                        <CellPrimary title={r.type}>{r.type}</CellPrimary>
                        <CellMutedWrap title={r.description}>{r.description}</CellMutedWrap>
                      </CellStack>
                    </Table.Cell>
                    <Table.Cell>
                      <div onClick={(e) => e.stopPropagation()} role="presentation">
                        <StatusCell>
                          {r.status === 'resolved' || r.status === 'completed' ? (
                            <StatusBadge variant={r.status === 'resolved' ? 'resolved' : 'completed'}>
                              {formatComplainStatusLabel(r.status)}
                            </StatusBadge>
                          ) : r.status === 'rejected' ? (
                            <StatusStack>
                              <StatusBadge variant="rejected">
                                {formatComplainStatusLabel('rejected')}
                              </StatusBadge>
                              <Select
                                aria-label="Update from rejected"
                                value=""
                                size="sm"
                                fullWidth
                                dark
                                disabled={statusSavingId === r.id}
                                onChange={(e) => {
                                  const v = e.target.value as ComplainStatus | '';
                                  if (v === 'completed') void handleStatusChange(r, 'completed');
                                }}
                              >
                                <option value="">Change status…</option>
                                <option value="completed">{formatStatusLabel('completed')}</option>
                              </Select>
                            </StatusStack>
                          ) : (
                            <Select
                              value={r.status}
                              size="sm"
                              fullWidth
                              dark
                              disabled={statusSavingId === r.id}
                              onChange={(e) => handleStatusChange(r, e.target.value as ComplainStatus)}
                            >
                              {COMPANY_COMPLAIN_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                  {formatStatusLabel(s)}
                                </option>
                              ))}
                            </Select>
                          )}
                        </StatusCell>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {r.status === 'rejected' && r.rejectionReason ? (
                        <DescText title={r.rejectionReason}>{r.rejectionReason}</DescText>
                      ) : (
                        '—'
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {complainUsePaging ? (
              <div
                style={{
                  marginTop: theme.spacing[4],
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: theme.spacing[3],
                }}
              >
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                  Showing {complainRangeStart}–{complainRangeEnd} of {complainTotalItems}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={complainPage <= 1}
                    onClick={() => setComplainPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.primary }}>
                    Page {complainPage} of {complainTotalPages}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={complainPage >= complainTotalPages}
                    onClick={() => setComplainPage((p) => Math.min(complainTotalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
            </>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title="Complain details"
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setDetail(null)}>
            Close
          </Button>
        }
      >
        {detail && (
          <DetailGrid>
            <DetailField>
              <DetailLabel>Complain ID</DetailLabel>
              <DetailValue>
                <Mono>{detail.id}</Mono>
              </DetailValue>
            </DetailField>
            <DetailField>
              <DetailLabel>Type</DetailLabel>
              <DetailValue>{detail.type}</DetailValue>
            </DetailField>
            <DetailField>
              <DetailLabel>Status</DetailLabel>
              <StatusCell>
                {detail.status === 'resolved' || detail.status === 'completed' ? (
                  <StatusBadge variant={detail.status === 'resolved' ? 'resolved' : 'completed'}>
                    {formatComplainStatusLabel(detail.status)}
                  </StatusBadge>
                ) : detail.status === 'rejected' ? (
                  <StatusStack>
                    <StatusBadge variant="rejected">{formatComplainStatusLabel('rejected')}</StatusBadge>
                    <Select
                      aria-label="Update from rejected"
                      value=""
                      size="sm"
                      fullWidth
                      dark
                      disabled={statusSavingId === detail.id}
                      onChange={(e) => {
                        const v = e.target.value as ComplainStatus | '';
                        if (v === 'completed') void handleStatusChange(detail, 'completed');
                      }}
                    >
                      <option value="">Change status…</option>
                      <option value="completed">{formatStatusLabel('completed')}</option>
                    </Select>
                  </StatusStack>
                ) : (
                  <Select
                    value={detail.status}
                    size="sm"
                    fullWidth
                    dark
                    disabled={statusSavingId === detail.id}
                    onChange={(e) => handleStatusChange(detail, e.target.value as ComplainStatus)}
                  >
                    {COMPANY_COMPLAIN_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {formatStatusLabel(s)}
                      </option>
                    ))}
                  </Select>
                )}
              </StatusCell>
            </DetailField>
            {detail.status === 'rejected' && detail.rejectionReason ? (
              <DetailField>
                <DetailLabel>Rejection reason</DetailLabel>
                <DetailValue>{detail.rejectionReason}</DetailValue>
              </DetailField>
            ) : null}
            <DetailField>
              <DetailLabel>Description</DetailLabel>
              <DetailValue>{detail.description}</DetailValue>
            </DetailField>
            <DetailField>
              <DetailLabel>Branch / bank</DetailLabel>
              <DetailValue>
                {[detail.bankName, detail.address].filter(Boolean).join(' — ') || '—'}
              </DetailValue>
            </DetailField>
            {detail.branchCode ? (
              <DetailField>
                <DetailLabel>Branch code</DetailLabel>
                <DetailValue>{detail.branchCode}</DetailValue>
              </DetailField>
            ) : null}
            <DetailField>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>{formatComplainDisplayDate(detail.createdAt)}</DetailValue>
            </DetailField>
            {complainDateToJs(effectiveCompletedAtForDetail(detail)) ? (
              <DetailField>
                <DetailLabel>Completed on</DetailLabel>
                <DetailValue>{formatComplainDisplayDate(effectiveCompletedAtForDetail(detail))}</DetailValue>
              </DetailField>
            ) : null}
            {complainDateToJs(effectiveResolvedAtForDetail(detail)) ? (
              <DetailField>
                <DetailLabel>Resolved on</DetailLabel>
                <DetailValue>{formatComplainDisplayDate(effectiveResolvedAtForDetail(detail))}</DetailValue>
              </DetailField>
            ) : null}
          </DetailGrid>
        )}
      </Modal>

      <Modal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filter by status"
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStatusFilters(allStatusesSelected())}
            >
              Show all statuses
            </Button>
            <Button type="button" onClick={() => setFilterModalOpen(false)}>
              Done
            </Button>
          </>
        }
      >
        <FilterCheckboxList>
          {COMPLAIN_STATUSES.map((s) => (
            <FilterCheckboxRow key={s}>
              <input
                type="checkbox"
                checked={statusFilters[s]}
                onChange={() =>
                  setStatusFilters((prev) => ({
                    ...prev,
                    [s]: !prev[s],
                  }))
                }
              />
              <span>{formatStatusLabel(s)}</span>
            </FilterCheckboxRow>
          ))}
        </FilterCheckboxList>
      </Modal>
    </DashboardLayout>
  );
};

export default CompanyComplainsPage;
